import React, { useState } from "react";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { formatDate } from "common/service/CommonService";
import { formatTime24Hour } from "modules/Session/Session/helper/sessionlogconstant";
import TotalAttemptsHistoryModal from "./TotalAttemptsHistoryModal";
import { renderResultBadge } from "../helper/attemptsConstant";

interface SampleSeparationHistoryTabProps {
  data: any[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
}

const SampleSeparationHistoryTab: React.FC<SampleSeparationHistoryTabProps> = ({
  data,
  loading,
  currentPage,
  pageSize,
  totalRecords,
  onPageChange,
}) => {
  const [selectedAttemptRow, setSelectedAttemptRow] = useState<any>(null);
  const [isAttemptsModalOpen, setIsAttemptsModalOpen] = useState<boolean>(false);

  const handleOpenAttemptsModal = (row: any) => {
    setSelectedAttemptRow(row);
    setIsAttemptsModalOpen(true);
  };

  const columns = [
    {
      key: "subjectno",
      label: "Subject No",
      width: "100px",
      textCenter: true,
      sticky: true,
      render: (_: any, row: any) => (
        <div className="fw-bold text-dark">{row.subjectno || "-"}</div>
      ),
    },
    {
      key: "vaccutainerbarcode",
      label: "Vacutainer Barcode",
      width: "165px",
      textCenter: true,
      sticky: true,
      render: (_: any, row: any) => (
        <div className="font-monospace fw-semibold text-dark">{row.vaccutainerbarcode || "-"}</div>
      ),
    },
    {
      key: "aliquotbarcode",
      label: "Aliquot Barcode",
      width: "130px",
      textCenter: true,
      sticky: true,
      render: (_: any, row: any) => (
        <div className="font-monospace text-primary">{row.aliquotbarcode || "-"}</div>
      ),
    },
    {
      key: "period_number",
      label: "Period",
      width: "90px",
      textCenter: true,
      sticky: true,
      render: (_: any, row: any) => {
        const pNum = row.period_number ? String(row.period_number).padStart(2, "0") : "-";
        return (
          <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 px-2 py-1 font-monospace fw-semibold">
            P:{pNum}
          </span>
        );
      },
    },
    {
      key: "timepoint_number",
      label: "Timepoint",
      width: "110px",
      textCenter: true,
      sticky: true,
      render: (_: any, row: any) => {
        const tpNum = row.timepoint_number || "-";
        return (
          <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2 py-1 font-monospace fw-semibold">
            T:{tpNum}
          </span>
        );
      },
    },
    {
      key: "aliquot_number",
      label: "Aliquot No",
      width: "100px",
      textCenter: true,
      sticky: true,
      divider: true,
      render: (_: any, row: any) => {
        const alNo = row.aliquot_number || "AS";
        return (
          <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 px-2 py-1 font-monospace fw-semibold">
            {alNo}
          </span>
        );
      },
    },
    {
      key: "totalattempts",
      label: "Total Attempts",
      textCenter: true,
      render: (_: any, row: any) => (
        <span
          className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2.5 py-1.5 fw-bold cursor-pointer"
          style={{ cursor: "pointer", transition: "all 0.2s ease" }}
          onClick={() => handleOpenAttemptsModal(row)}
          title="Click to view Total Attempts History"
        >
          <i className="ri-history-line me-1"></i>
          {row.totalattempts ?? 1}
        </span>
      ),
    },
    {
      key: "scanbyname",
      label: "Last Scanned By",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className="fw-semibold text-dark">{row.scanbyname || "—"}</div>
      ),
    },
    {
      key: "scandate",
      label: "Scanned Date & Time",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className="text-muted small">
          {row.scandate ? `${formatDate(row.scandate)} (${formatTime24Hour(row.scandate)})` : "—"}
        </div>
      ),
    },
    {
      key: "result",
      label: "Status",
      textCenter: true,
      render: (_: any, row: any) => renderResultBadge(row.result),
    },
    {
      key: "resultmessage",
      label: "Result",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className="text-muted small">{row.resultmessage || "Matched Successfully"}</div>
      ),
    },
  ];

  return (
    <>
      <CommonTableIndexWise
        columns={columns}
        data={data}
        loading={loading}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRecords={totalRecords}
        onPageChange={onPageChange}
        actions={{}}
      />

      {selectedAttemptRow && (
        <TotalAttemptsHistoryModal
          isOpen={isAttemptsModalOpen}
          toggle={() => setIsAttemptsModalOpen(!isAttemptsModalOpen)}
          type="separation"
          sessionid={selectedAttemptRow.sessionid}
          vaccutainerbarcode={selectedAttemptRow.vaccutainerbarcode}
          aliquotbarcode={selectedAttemptRow.aliquotbarcode}
          subjectno={selectedAttemptRow.subjectno}
        />
      )}
    </>
  );
};

export default SampleSeparationHistoryTab;
